import { useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapStore, setGlobalMapInstance } from '../stores/mapStore.js';

export function useMap() {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const { viewport, setViewport, flyTo } = useMapStore();

  const initMap = useCallback((container: HTMLDivElement, token: string) => {
    if (mapRef.current) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [viewport.lng, viewport.lat],
      zoom: viewport.zoom,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');
    const geolocate = new mapboxgl.GeolocateControl({ trackUserLocation: false });
    map.addControl(geolocate, 'top-right');
    map.once('load', () => geolocate.trigger());
    map.on('moveend', () => {
      const c = map.getCenter();
      setViewport({ lng: c.lng, lat: c.lat, zoom: map.getZoom() });
    });
    mapRef.current = map;
    setGlobalMapInstance(map);
    return map;
  }, [viewport, setViewport]);

  const flyToCoord = useCallback((lng: number, lat: number, zoom = 15) => {
    mapRef.current?.flyTo({ center: [lng, lat], zoom, speed: 1.5, curve: 1 });
    flyTo(lng, lat, zoom);
  }, [flyTo]);

  return { mapRef, initMap, flyToCoord };
}
